import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import SearchableSelect from './SearchableSelect';
import FileUploadComponent from './FileUploadComponent';
import { FileUploadService } from '../services/FileUploadService';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import Loader from './Loader';
import { formatDateForInputIST, formatDisplayDateIST, getTodayInputDateIST } from '../utils/dateUtils';

const getDuplicateSerialIndexes = (serialNumbers = []) => {
  const seen = new Map();
  const duplicates = new Set();

  serialNumbers.forEach((serial, index) => {
    const normalized = (serial || '').trim().toLowerCase();
    if (!normalized) return;

    if (seen.has(normalized)) {
      duplicates.add(index);
      duplicates.add(seen.get(normalized));
      return;
    }

    seen.set(normalized, index);
  });

  return duplicates;
};

const extractSerialFromNotes = (notes) => {
  if (!notes) return '';
  const match = String(notes).match(/Serial:\s*([^|]+)/i);
  return match ? match[1].trim() : '';
};

const sanitizeRepairNotes = (notes) => {
  if (!notes) return '';

  return String(notes)
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part && !/^serial\s*:/i.test(part) && !/^batch\s*:/i.test(part))
    .join(' | ');
};

const mergeAttachmentFileNames = (...values) => {
  const seen = new Set();
  const merged = [];

  values.forEach((value) => {
    String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((fileName) => {
        if (!seen.has(fileName)) {
          seen.add(fileName);
          merged.push(fileName);
        }
      });
  });

  return merged.join(', ');
};

function Repairs() {
  const [repairs, setRepairs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignableAssignedAssets, setAssignableAssignedAssets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetRepairs, setAssetRepairs] = useState([]);
  const [repairStats, setRepairStats] = useState({ all: 0, pending: 0, inRepair: 0, completed: 0, totalCost: 0, monthCost: 0 });
  const [assetRepairStats, setAssetRepairStats] = useState({ totalRepairs: 0, totalCost: 0 });
  const [statusFilter, setStatusFilter] = useState('All');
  const [groupByDate, setGroupByDate] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [groupedCurrentPage, setGroupedCurrentPage] = useState(1);
  const [groupedItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    assetId: '',
    employeeId: '',
    repairDate: getTodayInputDateIST(),
    repairReason: '',
    status: 'Pending',
    repairCost: 0,
    repairVendor: '',
    billNumber: '',
    notes: '',
    repairSource: 0,
    attachmentFileNames: '',
    completedDate: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [repairType, setRepairType] = useState('assigned'); // 'assigned' or 'office'
  const [officeRepairData, setOfficeRepairData] = useState({
    selectedAsset: null,
    repairQuantity: 1,
    serialNumbers: ['']
  });
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchRepairs(),
        fetchRepairStats(),
        fetchAssignableAssignedAssets(),
        fetchAssets(),
        fetchEmployees(),
        fetchVendors(),
        fetchAssignments()
      ]);
      setLoading(false);
    };
    document.title = 'Repairs - Saral Asset Manager';
    loadData();
  }, []);

  const fetchRepairs = async () => {
    try {
      const response = await axios.get('/api/repairs');
      console.log('Repairs data:', response.data);
      setRepairs((response.data || []).filter(r => (r.asset?.status || r.asset?.Status) !== 'Dead'));
    } catch (error) {
      console.error('Error fetching repairs:', error);
      setRepairs([]);
    }
  };

  const fetchRepairStats = async () => {
    try {
      const response = await axios.get('/api/repairs/stats');
      const data = response.data || {};
      setRepairStats({
        all: data.all ?? data.totalRepairs ?? 0,
        pending: data.pending ?? 0,
        inRepair: data.inRepair ?? 0,
        completed: data.completed ?? 0,
        totalCost: data.totalCost ?? 0,
        monthCost: data.monthCost ?? 0
      });
    } catch (error) {
      console.error('Error fetching repair stats:', error);
      setRepairStats({ all: 0, pending: 0, inRepair: 0, completed: 0, totalCost: 0, monthCost: 0 });
    }
  };

  const fetchAssetRepairs = async (assetId) => {
    try {
      const [repairsResponse, statsResponse] = await Promise.all([
        axios.get(`/api/repairs/asset/${assetId}`),
        axios.get(`/api/repairs/asset/${assetId}/stats`)
      ]);
      setAssetRepairs(repairsResponse.data || []);
      const stats = statsResponse.data || {};
      setAssetRepairStats({
        totalRepairs: stats.totalRepairs ?? 0,
        totalCost: stats.totalCost ?? 0
      });
    } catch (error) {
      console.error('Error fetching asset repairs:', error);
      setAssetRepairs([]);
      setAssetRepairStats({ totalRepairs: 0, totalCost: 0 });
    }
  };

  const fetchAssignableAssignedAssets = async () => {
    try {
      const response = await axios.get('/api/repairs/assignable-assigned-assets');
      setAssignableAssignedAssets(response.data || []);
    } catch (error) {
      console.error('Error fetching assignable assigned assets:', error);
      setAssignableAssignedAssets([]);
    }
  };

  const handleViewHistory = (repair) => {
    const asset = repair.asset || repair.Asset;
    setSelectedAsset(asset);
    fetchAssetRepairs(repair.assetId || repair.AssetId);
    setShowHistoryModal(true);
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get('/api/assets');
      setAssets((response.data || []).filter(asset => asset.status !== 'Dead'));
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/api/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get('/api/assetassignments');
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleAssetChange = (assetId) => {
    // Handle both direct value and event object
    const actualAssetId = assetId?.target ? assetId.target.value : assetId;
    console.log('Selected asset ID:', actualAssetId);
    const activeAssignment = assignments.find(a => a.assetId == actualAssetId && a.isActive);
    if (activeAssignment) {
      setFormData({
        ...formData,
        assetId: actualAssetId,
        employeeId: activeAssignment.employeeId,
        repairSource: 1
      });
    } else {
      setFormData({
        ...formData,
        assetId: actualAssetId,
        employeeId: '',
        repairSource: 0
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (repairType === 'office') {
      const duplicateSerialIndexes = getDuplicateSerialIndexes(officeRepairData.serialNumbers);
      if (duplicateSerialIndexes.size > 0) {
        showAlert('danger', 'Duplicate serial numbers are not allowed. Please use unique serial numbers.');
        return;
      }

      const selectedAssetId = officeRepairData.selectedAsset?.id;
      const enteredSerials = officeRepairData.serialNumbers
        .map((s) => (s || '').trim().toLowerCase())
        .filter(Boolean);
      const openStatuses = new Set(['pending', 'inrepair']);
      const existingOpenSerials = new Set(
        repairs
          .filter((r) => {
            const repairId = r?.id ?? r?.Id;
            const assetId = r?.assetId ?? r?.AssetId;
            const status = (r?.status ?? r?.Status ?? '').toString().toLowerCase();
            return (
              String(assetId) === String(selectedAssetId) &&
              openStatuses.has(status) &&
              (!editingRepair || String(repairId) !== String(editingRepair.id || editingRepair.Id))
            );
          })
          .map((r) => extractSerialFromNotes(r?.notes ?? r?.Notes).toLowerCase())
          .filter(Boolean)
      );
      const conflictingSerials = enteredSerials.filter((s) => existingOpenSerials.has(s));
      if (conflictingSerials.length > 0) {
        const uniqueConflicts = [...new Set(conflictingSerials)];
        showAlert('danger', `Open repair already exists for serial: ${uniqueConflicts.join(', ')}`);
        return;
      }
    }

    setSaving(true);

    const normalizeRepairPayload = (repairData) => {
      const payload = { ...repairData };
      // API DTO binding expects null for optional date fields, not empty strings.
      payload.repairDate = payload.repairDate || null;
      payload.completedDate = payload.completedDate || null;
      // Ensure numeric cost and remove UI-only helper fields.
      payload.repairCost = parseFloat(payload.repairCost) || 0;
      delete payload.attachmentFileNames;
      delete payload.serialNumber;
      return payload;
    };

    try {
      let fileNames = '';
      
      if (!editingRepair && repairType === 'office' && officeRepairData.selectedAsset && officeRepairData.serialNumbers.length > 1) {
        // Create multiple repair records for office repairs (one per serial).
        const batchId = `BATCH_${Date.now()}`;
        const costPerItem = parseFloat(formData.repairCost) || 0;
        
        for (let i = 0; i < officeRepairData.serialNumbers.length; i++) {
          const serialNumber = officeRepairData.serialNumbers[i];
          
          let individualFileNames = '';
          if (selectedFiles.length > 0) {
            const folderName = `repair_${officeRepairData.selectedAsset.id}_${Date.now()}_${i}`;
            try {
              individualFileNames = await FileUploadService.uploadFiles(selectedFiles, folderName);
            } catch (uploadError) {
              console.error('File upload failed:', uploadError);
            }
          }
          
          const repairData = {
            ...formData,
            assetId: officeRepairData.selectedAsset.id,
            employeeId: '',
            attachmentFileName: individualFileNames,
            repairCost: costPerItem,
            notes: `${formData.notes}${formData.notes ? ' | ' : ''}Serial: ${serialNumber} | Batch: ${batchId}`,
            serialNumber
          };
          
          await axios.post('/api/repairs', normalizeRepairPayload(repairData));
        }
      } else {
        if (selectedFiles.length > 0) {
          const assetId = repairType === 'office' ? officeRepairData.selectedAsset?.id : formData.assetId;
          const folderName = `repair_${assetId}_${editingRepair ? editingRepair.id : Date.now()}`;
          try {
            fileNames = await FileUploadService.uploadFiles(selectedFiles, folderName);
          } catch (uploadError) {
            console.error('File upload failed:', uploadError);
          }
        }

        const repairData = {
          ...formData,
          assetId: repairType === 'office' ? officeRepairData.selectedAsset?.id : formData.assetId,
          employeeId: repairType === 'office' ? '' : formData.employeeId,
          attachmentFileName: editingRepair
            ? mergeAttachmentFileNames(formData.attachmentFileName, fileNames)
            : fileNames,
          repairCost: parseFloat(formData.repairCost) || 0,
          notes: repairType === 'office' && officeRepairData.serialNumbers[0]
            ? `${sanitizeRepairNotes(formData.notes)}${sanitizeRepairNotes(formData.notes) ? ' | ' : ''}Serial: ${officeRepairData.serialNumbers[0]}`
            : formData.notes
        };

        if (editingRepair) {
          await axios.put(`/api/repairs/${editingRepair.id}`, normalizeRepairPayload(repairData));
        } else {
          await axios.post('/api/repairs', normalizeRepairPayload(repairData));
        }
      }
      
      await Promise.all([fetchRepairs(), fetchRepairStats(), fetchAssignableAssignedAssets()]);
      handleCloseModal();
      showAlert('success', editingRepair ? 'Repair updated successfully!' : 'Repair created successfully!');
    } catch (error) {
      console.error('Error saving repair:', error);
      const errorMessage = error?.response?.data || error?.message || 'Error saving repair';
      showAlert('danger', String(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (repair) => {
    setEditingRepair(repair);
    const asset = repair.asset || repair.Asset;
    const repairSource = repair.repairSource ?? repair.RepairSource ?? 0;
    const existingAttachmentFileName = repair.attachmentFileName || repair.AttachmentFileName || '';
    setFormData({
      assetId: repair.assetId || repair.AssetId || '',
      employeeId: repair.employeeId || repair.EmployeeId || '',
      repairDate: formatDateForInputIST(repair.repairDate || repair.RepairDate) || getTodayInputDateIST(),
      repairReason: repair.repairReason || repair.RepairReason || '',
      status: repair.status || repair.Status || 'Pending',
      repairCost: repair.repairCost || repair.RepairCost || 0,
      repairVendor: repair.repairVendor || repair.RepairVendor || '',
      billNumber: repair.billNumber || repair.BillNumber || '',
      notes: sanitizeRepairNotes(repair.notes || repair.Notes || ''),
      repairSource,
      attachmentFileName: existingAttachmentFileName,
      completedDate: formatDateForInputIST(repair.completedDate || repair.CompletedDate) || ''
    });
    setSelectedFiles([]);
    setRepairType(repairSource === 0 ? 'office' : 'assigned');
    setOfficeRepairData({
      selectedAsset: repairSource === 0 && asset ? {
        id: asset.id || asset.Id,
        name: asset.name || asset.Name,
        quantity: asset.quantity || asset.Quantity || 1,
        assignedCount: asset.assignedCount || asset.AssignedCount || 0,
        deadCount: asset.deadCount || asset.DeadCount || 0
      } : null,
      repairQuantity: 1,
      serialNumbers: [extractSerialFromNotes(repair.notes || repair.Notes || '')]
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this repair record?')) {
      try {
        await axios.delete(`/api/repairs/${id}`);
        await Promise.all([fetchRepairs(), fetchRepairStats(), fetchAssignableAssignedAssets()]);
        showAlert('success', 'Repair deleted successfully!');
      } catch (error) {
        console.error('Error deleting repair:', error);
        showAlert('danger', 'Error deleting repair');
      }
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`/api/repairs/${id}`)));
      setShowDeleteSelectedConfirm(false);
      setSelectedIds([]);
      showAlert('success', `${selectedIds.length} repair(s) deleted successfully!`);
      await Promise.all([fetchRepairs(), fetchRepairStats(), fetchAssignableAssignedAssets()]);
    } catch (error) {
      console.error('Error deleting selected repairs:', error);
      showAlert('danger', 'Failed to delete selected repairs');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRepairs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRepairs.map(r => r.id || r.Id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRepair(null);
    setSelectedFiles([]);
    setRepairType('assigned');
    setOfficeRepairData({
      selectedAsset: null,
      repairQuantity: 1,
      serialNumbers: ['']
    });
    setFormData({
      assetId: '',
      employeeId: '',
      repairDate: getTodayInputDateIST(),
      repairReason: '',
      status: 'Pending',
      repairCost: 0,
      repairVendor: '',
      billNumber: '',
      notes: '',
      repairSource: 0,
      attachmentFileName: '',
      completedDate: ''
    });
  };

  const getRepairDateValue = (repair) => repair?.repairDate || repair?.RepairDate;

  const getRepairDateKey = (repair) => {
    const dateValue = getRepairDateValue(repair);
    return formatDateForInputIST(dateValue) || 'Unknown Date';
  };

  const getRepairDateDisplay = (repair) => {
    const dateValue = getRepairDateValue(repair);
    return formatDisplayDateIST(dateValue);
  };

  const getDateKeyDisplay = (dateKey) => {
    if (dateKey === 'Unknown Date') return dateKey;
    return formatDisplayDateIST(dateKey);
  };

  const getRepairAssetLabel = (row) => {
    if (!row) return 'N/A';
    const asset = row.asset || row.Asset;
    if (!asset) return 'N/A';
    const name = asset.name || asset.Name || '';
    const assignment = assignments.find(a => a.assetId === asset.id && a.employeeId === row.employeeId);
    let serial = assignment?.serialNumber || 'No Serial';
    const notes = row.notes || row.Notes || '';
    const serialMatch = notes.match(/Serial: ([^|]+)/);
    if (serialMatch) {
      serial = serialMatch[1].trim();
    }
    const batchMatch = notes.match(/Batch: ([^|]+)/);
    const batchInfo = batchMatch ? ` (${batchMatch[1].trim()})` : '';
    return `${name} - ${serial}${batchInfo}`;
  };

  const getRepairEmployeeLabel = (row) => {
    if (!row) return '-';
    return row.employeeName || row.EmployeeName || row.employeeId || row.EmployeeId || '-';
  };

  const getRepairStatus = (row) => row?.status || row?.Status || 'Pending';

  const getStatusBadgeClass = (status) => (
    status === 'Dead' ? 'danger' : status === 'Completed' ? 'success' : status === 'InRepair' ? 'warning' : 'secondary'
  );

  const filteredRepairs = repairs.filter(repair => {
    const status = repair.status || repair.Status;
    const matchesStatus = statusFilter === 'All' || status === statusFilter;
    return matchesStatus;
  });

  const columns = [
    { 
      field: 'checkbox', 
      label: (
        <input 
          type="checkbox" 
          checked={selectedIds.length > 0 && selectedIds.length === filteredRepairs.length}
          onChange={toggleSelectAll}
        />
      ),
      render: (value, item) => (
        <input 
          type="checkbox" 
          checked={selectedIds.includes(item.id || item.Id)}
          onChange={() => toggleSelect(item.id || item.Id)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    { field: 'id', label: 'ID' },
    { field: 'asset', label: 'Asset', render: (value, row) => getRepairAssetLabel(row) },
    { field: 'assetType', label: 'Asset Type', render: (value, row) => {
      const asset = row.asset || row.Asset;
      const assetType = asset?.assetType || asset?.AssetType;
      return assetType?.name || assetType?.Name || 'N/A';
    }},
    { field: 'employee', label: 'Employee', render: (value, row) => getRepairEmployeeLabel(row) },
    { field: 'repairReason', label: 'Reason', render: (value, row) => row?.repairReason || row?.RepairReason || '-' },
    { field: 'status', label: 'Status', render: (value, row) => {
      const status = getRepairStatus(row);
      return (
        <span className={`badge bg-${getStatusBadgeClass(status)}`}>
          {status}
        </span>
      );
    }},
    { field: 'repairCost', label: 'Cost', render: (value, row) => {
      const cost = row?.repairCost || row?.RepairCost || 0;
      return `₹${parseFloat(cost).toFixed(2)}`;
    }},
    { field: 'repairVendor', label: 'Vendor', render: (value, row) => row?.repairVendor || row?.RepairVendor || '-' },
    { field: 'billNumber', label: 'Bill #', render: (value, row) => row?.billNumber || row?.BillNumber || '-' },
    { field: 'attachment', label: 'Files', render: (value, row) => {
      const fileNames = row?.attachmentFileName || row?.AttachmentFileName;
      if (!fileNames) return '-';
      
      const folderName = `repair_${row.assetId}_${row.id}`;
      return (
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={() => FileUploadService.downloadFiles(folderName)}
        >
          <i className="fas fa-download me-1"></i>
          {fileNames.split(',').length} file(s)
        </button>
      );
    }},
    { field: 'repairDate', label: 'Date', render: (value, row) => getRepairDateDisplay(row) },
    { field: 'completedDate', label: 'Completed', render: (value, row) => {
      const date = row?.completedDate || row?.CompletedDate;
      return date ? formatDisplayDateIST(date) : '-';
    }}
  ];

  const groupedDateKeys = [...new Set(filteredRepairs.map(getRepairDateKey))];
  const areAllDateGroupsExpanded = groupedDateKeys.length > 0 && groupedDateKeys.every(dateKey => expandedDates.has(dateKey));

  useEffect(() => {
    if (!groupByDate) {
      setExpandedDates(new Set());
      setGroupedCurrentPage(1);
      return;
    }

    const validDateKeys = new Set(filteredRepairs.map(getRepairDateKey));
    setExpandedDates(prev => new Set([...prev].filter(dateKey => validDateKeys.has(dateKey))));
    setGroupedCurrentPage(1);
  }, [groupByDate, statusFilter, repairs]);

  const toggleDateGroup = (dateKey) => {
    const nextExpanded = new Set(expandedDates);
    if (nextExpanded.has(dateKey)) {
      nextExpanded.delete(dateKey);
    } else {
      nextExpanded.add(dateKey);
    }
    setExpandedDates(nextExpanded);
  };

  const toggleAllDateGroups = () => {
    if (areAllDateGroupsExpanded) {
      setExpandedDates(new Set());
    } else {
      setExpandedDates(new Set(groupedDateKeys));
    }
  };

  const statusCounts = {
    All: repairStats.all,
    Pending: repairStats.pending,
    InRepair: repairStats.inRepair,
    Completed: repairStats.completed
  };
  const duplicateOfficeSerialIndexes = repairType === 'office'
    ? getDuplicateSerialIndexes(officeRepairData.serialNumbers)
    : new Set();

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="page-header mb-4">
        <h1 className="page-title mb-0">Asset Repairs</h1>
        <div className="page-header-actions">
          {!groupByDate && selectedIds.length > 0 && (
            <button 
              className="btn btn-sm btn-danger me-2"
              onClick={() => setShowDeleteSelectedConfirm(true)}
            >
              <i className="fas fa-trash-alt me-1"></i>Delete Selected ({selectedIds.length})
            </button>
          )}
          <button
            className={`btn btn-sm ${groupByDate ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => {
              setGroupByDate(!groupByDate);
              setSelectedIds([]);
            }}
          >
            <i className={`fas fa-${groupByDate ? 'calendar-alt' : 'list'} me-2`}></i>
            {groupByDate ? 'Grouped' : 'List'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus me-2"></i>Create Repair
          </button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className={`card border-0 shadow-sm cursor-pointer ${statusFilter === 'All' ? 'border-primary border-2' : ''}`} 
               onClick={() => setStatusFilter('All')} style={{cursor: 'pointer'}}>
            <div className="card-body text-center">
              <h3 className="text-primary mb-0">{statusCounts.All}</h3>
              <small className="text-muted">All Repairs</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card border-0 shadow-sm cursor-pointer ${statusFilter === 'Pending' ? 'border-secondary border-2' : ''}`}
               onClick={() => setStatusFilter('Pending')} style={{cursor: 'pointer'}}>
            <div className="card-body text-center">
              <h3 className="text-secondary mb-0">{statusCounts.Pending}</h3>
              <small className="text-muted">Pending</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card border-0 shadow-sm cursor-pointer ${statusFilter === 'InRepair' ? 'border-warning border-2' : ''}`}
               onClick={() => setStatusFilter('InRepair')} style={{cursor: 'pointer'}}>
            <div className="card-body text-center">
              <h3 className="text-warning mb-0">{statusCounts.InRepair}</h3>
              <small className="text-muted">In Repair</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`card border-0 shadow-sm cursor-pointer ${statusFilter === 'Completed' ? 'border-success border-2' : ''}`}
               onClick={() => setStatusFilter('Completed')} style={{cursor: 'pointer'}}>
            <div className="card-body text-center">
              <h3 className="text-success mb-0">{statusCounts.Completed}</h3>
              <small className="text-muted">Completed</small>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className={groupByDate ? 'card-body p-0' : 'card-body'}>
          {loading ? (
            <div className="table-loader">
              <Loader text="Loading repairs..." size="medium" />
            </div>
          ) : groupByDate ? (
            <div className="p-3">
              <div className="mb-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleAllDateGroups}
                >
                  <i className={`fas fa-${areAllDateGroupsExpanded ? 'minus' : 'plus'}-square me-1`}></i>
                  {areAllDateGroupsExpanded ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
              {(() => {
                const grouped = filteredRepairs.reduce((acc, repair) => {
                  const dateKey = getRepairDateKey(repair);
                  if (!acc[dateKey]) acc[dateKey] = [];
                  acc[dateKey].push(repair);
                  return acc;
                }, {});

                const entries = Object.entries(grouped);
                if (entries.length === 0) return <div className="text-center py-5"><p>No repairs</p></div>;

                const totalPages = Math.max(1, Math.ceil(entries.length / groupedItemsPerPage));
                const currentPage = Math.min(groupedCurrentPage, totalPages);
                const pageStart = (currentPage - 1) * groupedItemsPerPage;
                const pagedEntries = entries.slice(pageStart, pageStart + groupedItemsPerPage);

                return (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">
                        Showing {pagedEntries.length} of {entries.length} dates
                      </small>
                      <small className="text-muted">Page {currentPage} / {totalPages}</small>
                    </div>

                    {pagedEntries.map(([dateKey, dateRepairs]) => {
                      const isExpanded = expandedDates.has(dateKey);
                      return (
                        <div key={dateKey} className="card mb-3 shadow-sm" style={{ border: '1px solid #dee2e6', overflow: 'visible' }}>
                          <div
                            className="card-header border-bottom d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer', borderRadius: isExpanded ? '0.375rem 0.375rem 0 0' : '0.375rem' }}
                            onClick={() => toggleDateGroup(dateKey)}
                          >
                            <div>
                              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                              <i className="fas fa-calendar me-2"></i>
                              <strong>{getDateKeyDisplay(dateKey)}</strong>
                            </div>
                            <span className="badge bg-primary text-white">{dateRepairs.length} repairs</span>
                          </div>

                          {isExpanded && (
                            <div className="card-body p-0" style={{ overflow: 'visible' }}>
                              <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>ID</th>
                                      <th>Asset</th>
                                      <th>Asset Type</th>
                                      <th>Employee</th>
                                      <th>Reason</th>
                                      <th>Status</th>
                                      <th>Cost</th>
                                      <th>Vendor</th>
                                      <th>Bill #</th>
                                      <th>Date</th>
                                      <th>Completed</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dateRepairs.map((repair) => {
                                      const status = getRepairStatus(repair);
                                      const asset = repair.asset || repair.Asset;
                                      const assetType = asset?.assetType || asset?.AssetType;
                                      const completedDate = repair.completedDate || repair.CompletedDate;
                                      return (
                                        <tr key={repair.id || repair.Id}>
                                          <td>{repair.id || repair.Id}</td>
                                          <td>{getRepairAssetLabel(repair)}</td>
                                          <td>{assetType?.name || assetType?.Name || 'N/A'}</td>
                                          <td>{getRepairEmployeeLabel(repair)}</td>
                                          <td>{repair.repairReason || repair.RepairReason || '-'}</td>
                                          <td>
                                            <span className={`badge bg-${getStatusBadgeClass(status)}`}>
                                              {status}
                                            </span>
                                          </td>
                                          <td>₹{parseFloat(repair.repairCost || repair.RepairCost || 0).toFixed(2)}</td>
                                          <td>{repair.repairVendor || repair.RepairVendor || '-'}</td>
                                          <td>{repair.billNumber || repair.BillNumber || '-'}</td>
                                          <td>{getRepairDateDisplay(repair)}</td>
                                          <td>{completedDate ? formatDisplayDateIST(completedDate) : '-'}</td>
                                          <td>
                                            <div className="dropdown">
                                              <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown">
                                                <i className="fas fa-ellipsis-v"></i>
                                              </button>
                                              <ul className="dropdown-menu dropdown-menu-end">
                                                <li>
                                                  <button className="dropdown-item" onClick={() => handleViewHistory(repair)}>
                                                    <i className="fas fa-history me-2"></i>View History
                                                  </button>
                                                </li>
                                                {status !== 'Completed' && (
                                                  <>
                                                    <li>
                                                      <button className="dropdown-item" onClick={() => handleEdit(repair)}>
                                                        <i className="fas fa-edit me-2"></i>Edit
                                                      </button>
                                                    </li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    <li>
                                                      <button className="dropdown-item text-danger" onClick={() => handleDelete(repair.id || repair.Id)}>
                                                        <i className="fas fa-trash me-2"></i>Delete
                                                      </button>
                                                    </li>
                                                  </>
                                                )}
                                              </ul>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {totalPages > 1 && (
                      <nav>
                        <ul className="pagination justify-content-center mt-2">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const maxVisible = 5;
                            const halfVisible = Math.floor(maxVisible / 2);
                            let startPage = Math.max(1, currentPage - halfVisible);
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                            if (endPage - startPage < maxVisible - 1) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }

                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => setGroupedCurrentPage(i)}>{i}</button>
                                </li>
                              );
                            }
                            return pages;
                          })()}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredRepairs}
              onEdit={handleEdit}
              onDelete={handleDelete}
              hideEdit={(repair) => getRepairStatus(repair) === 'Completed'}
              hideDelete={(repair) => getRepairStatus(repair) === 'Completed'}
              customActions={(repair) => (
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleViewHistory(repair)}
                  >
                    <i className="fas fa-history me-2"></i>View History
                  </button>
                </li>
              )}
            />
          )}
        </div>
      </div>

      {showHistoryModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-history me-2"></i>
                  Repair History - {selectedAsset?.name || selectedAsset?.Name} ({selectedAsset?.serialNumber || selectedAsset?.SerialNumber})
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <strong>Total Repairs:</strong> {assetRepairStats.totalRepairs} | 
                  <strong className="ms-3">Total Cost:</strong> ₹{Number(assetRepairStats.totalCost || 0).toFixed(2)}
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Reason</th>
                        <th>Status</th>
                        <th>Cost</th>
                        <th>Vendor</th>
                        <th>Bill #</th>
                        <th>Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetRepairs.map((repair, idx) => (
                        <tr key={idx}>
                          <td>{formatDisplayDateIST(repair.repairDate || repair.RepairDate)}</td>
                          <td>{repair.repairReason || repair.RepairReason}</td>
                          <td>
                            <span className={`badge bg-${(repair.status || repair.Status) === 'Completed' ? 'success' : (repair.status || repair.Status) === 'InRepair' ? 'warning' : 'secondary'}`}>
                              {repair.status || repair.Status}
                            </span>
                          </td>
                          <td>₹{(repair.repairCost || repair.RepairCost || 0).toFixed(2)}</td>
                          <td>{repair.repairVendor || repair.RepairVendor || '-'}</td>
                          <td>{repair.billNumber || repair.BillNumber || '-'}</td>
                          <td>
                            {getRepairEmployeeLabel(repair)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg repair-modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingRepair ? 'Edit Repair' : 'Create Repair'}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              
              {!editingRepair && (
                <div className="modal-body border-bottom py-2">
                  <div className="d-flex justify-content-center">
                    <div className="btn-group" role="group">
                      <input 
                        type="radio" 
                        className="btn-check" 
                        name="repairType" 
                        id="assignedRepair" 
                        checked={repairType === 'assigned'}
                        onChange={() => setRepairType('assigned')}
                      />
                      <label className="btn btn-outline-primary" htmlFor="assignedRepair">
                        <i className="fas fa-user me-2"></i>Assigned Asset
                      </label>
                      
                      <input 
                        type="radio" 
                        className="btn-check" 
                        name="repairType" 
                        id="officeRepair" 
                        checked={repairType === 'office'}
                        onChange={() => {
                          setRepairType('office');
                          setFormData(prev => ({ ...prev, employeeId: '', assetId: '', repairSource: 0 }));
                        }}
                      />
                      <label className="btn btn-outline-primary" htmlFor="officeRepair">
                        <i className="fas fa-tools me-2"></i>Office Inventory
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {repairType === 'assigned' ? (
                    // Assigned Asset Repair Form
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Asset *</label>
                        <SearchableSelect
                          options={assignableAssignedAssets.map(item => ({
                            value: item.assetId,
                            label: `${item.assetName} - ${item.serialNumber || 'No Serial'}`
                          }))}
                          value={formData.assetId}
                          onChange={handleAssetChange}
                          placeholder="Select Assigned Asset"
                          required
                        />
                      </div>
                      {formData.repairSource !== 0 && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Employee *</label>
                          <SearchableSelect
                            options={employees.map(emp => ({ 
                              value: emp.id, 
                              label: emp.userName || emp.UserName || (emp.id ?? emp.Id)
                            }))}
                            value={formData.employeeId}
                            onChange={(value) => {
                              const actualValue = value?.target ? value.target.value : value;
                              setFormData({ ...formData, employeeId: actualValue });
                            }}
                            placeholder="Select Employee"
                            required
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    // Office Inventory Repair Form
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Asset *</label>
                        <SearchableSelect
                          options={assets.filter(asset => {
                            const available = asset.quantity - (asset.assignedCount || 0) - (asset.deadCount || 0);
                            return available > 0;
                          }).map(asset => ({
                            value: asset.id,
                            label: `${asset.name} (Available: ${asset.quantity - (asset.assignedCount || 0) - (asset.deadCount || 0)})`
                          }))}
                          value={officeRepairData.selectedAsset?.id || ''}
                          onChange={(e) => {
                            const asset = assets.find(a => a.id == e.target.value);
                            setOfficeRepairData({...officeRepairData, selectedAsset: asset, repairQuantity: 1, serialNumbers: ['']});
                          }}
                          placeholder="Select Asset from Inventory"
                          required
                        />
                      </div>
                      {officeRepairData.selectedAsset && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            max={officeRepairData.selectedAsset.quantity - (officeRepairData.selectedAsset.assignedCount || 0) - (officeRepairData.selectedAsset.deadCount || 0)}
                            className="form-control"
                            value={officeRepairData.repairQuantity}
                            onChange={(e) => {
                              const qty = parseInt(e.target.value) || 1;
                              if (qty > 0) {
                                const newSerials = Array(qty).fill('').map((_, i) => 
                                  officeRepairData.serialNumbers[i] || `${officeRepairData.selectedAsset.name}-${Date.now()}-${i + 1}`
                                );
                                setOfficeRepairData({...officeRepairData, repairQuantity: qty, serialNumbers: newSerials});
                              }
                            }}
                            required
                          />
                        </div>
                      )}
                      {officeRepairData.selectedAsset && officeRepairData.serialNumbers.length > 0 && (
                        <div className="col-12 mb-3">
                          <label className="form-label">Serial Numbers *</label>
                          {officeRepairData.repairQuantity > 1 && (
                            <div className="alert alert-info py-2 mb-2">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>Note:</strong> {officeRepairData.repairQuantity} separate repair records will be created for individual tracking.
                            </div>
                          )}
                          <div className="row g-2">
                            {officeRepairData.serialNumbers.map((serial, index) => (
                              <div key={index} className="col-md-4">
                                <input
                                  type="text"
                                  className={`form-control ${duplicateOfficeSerialIndexes.has(index) ? 'is-invalid' : ''}`}
                                  placeholder={`Serial ${index + 1}`}
                                  value={serial}
                                  onChange={(e) => {
                                    const newSerials = [...officeRepairData.serialNumbers];
                                    newSerials[index] = e.target.value;
                                    setOfficeRepairData({...officeRepairData, serialNumbers: newSerials});
                                  }}
                                  required
                                />
                              </div>
                            ))}
                          </div>
                          {duplicateOfficeSerialIndexes.size > 0 && (
                            <div className="text-danger small mt-2">
                              Duplicate serial numbers found. Each serial must be unique.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Common form fields for both repair types */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Repair Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.repairDate}
                        onChange={(e) => setFormData({ ...formData, repairDate: e.target.value })}
                        required
                      />
                    </div>

                    {editingRepair && (formData.status === 'Completed' || formData.status === 'Dead') && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Completion Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.completedDate}
                          onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
                        />
                      </div>
                    )}

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Repair Reason *</label>
                      <textarea
                        className="form-control"
                        value={formData.repairReason}
                        onChange={(e) => setFormData({ ...formData, repairReason: e.target.value })}
                        required
                        rows="2"
                      />
                    </div>

                    {editingRepair && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <SearchableSelect
                          options={[
                            { value: 'Pending', label: 'Pending' },
                            { value: 'InRepair', label: 'In Repair' },
                            { value: 'Completed', label: 'Completed' },
                            { value: 'Dead', label: 'Dead' }
                          ]}
                          value={formData.status}
                          onChange={(value) => {
                            const actualValue = value?.target ? value.target.value : value;
                            setFormData({ ...formData, status: actualValue });
                          }}
                          placeholder="Select Status"
                        />
                      </div>
                    )}

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Repair Cost (₹) - {repairType === 'office' && officeRepairData.repairQuantity > 1 ? 'Per item' : 'Total'}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.repairCost}
                        onChange={(e) => setFormData({ ...formData, repairCost: e.target.value })}
                        step="0.01"
                      />
                      {repairType === 'office' && officeRepairData.repairQuantity > 1 && (
                        <small className="text-muted">
                          Total cost: ₹{(parseFloat(formData.repairCost) * officeRepairData.repairQuantity || 0).toFixed(2)}
                        </small>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Repair Vendor</label>
                      <SearchableSelect
                        options={vendors.map(vendor => ({ value: vendor.name, label: vendor.name }))}
                        value={formData.repairVendor}
                        onChange={(value) => {
                          const actualValue = value?.target ? value.target.value : value;
                          setFormData({ ...formData, repairVendor: actualValue });
                        }}
                        placeholder="Select or add vendor"
                        onQuickAdd={async (name) => {
                          try {
                            const response = await axios.post('/api/vendors', {
                              name: name,
                              contactPerson: '',
                              phone: '',
                              email: '',
                              address: ''
                            });
                            await fetchVendors();
                            return { value: response.data.name, label: response.data.name };
                          } catch (error) {
                            console.error('Error adding vendor:', error);
                            throw error;
                          }
                        }}
                        addButtonText="Add Vendor"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bill Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.billNumber}
                        onChange={(e) => setFormData({ ...formData, billNumber: e.target.value })}
                      />
                    </div>

                    <FileUploadComponent
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      existingFiles={editingRepair && formData.attachmentFileName ? formData.attachmentFileName : null}
                      folderName={editingRepair ? `repair_${formData.assetId}_${editingRepair.id}` : null}
                      label="Upload Bill/Receipt (Optional)"
                    />

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows="2"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Repair Initiated By</label>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={0}
                              checked={formData.repairSource === 0}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value), employeeId: '' })}
                            />
                            <label className="form-check-label">Office</label>
                            <small className="d-block text-muted">Office identified issue and sending for repair</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={1}
                              checked={formData.repairSource === 1}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value) })}
                            />
                            <label className="form-check-label">Employee Reported</label>
                            <small className="d-block text-muted">Employee reported issue, asset still with employee</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={2}
                              checked={formData.repairSource === 2}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value) })}
                            />
                            <label className="form-check-label">Employee Returned</label>
                            <small className="d-block text-muted">Employee returned asset to office for repair</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                  <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                    {saving ? <Loader size="small" /> : (editingRepair ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className={`modal fade ${showDeleteSelectedConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteSelectedConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete Selected</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteSelectedConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete {selectedIds.length} selected repair(s) permanently!</p>
              <p>This action cannot be undone. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteSelectedConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Repairs;
